import React from "react";
import { Package, Zap, Shield, Feather, Tag, ExternalLink as ExternalLinkIcon } from "lucide-react";
import ExternalLink from "../components/ExternalLink";

const CLIQ = {
  title: "CLIQ Portable Chair",
  headline: "The Ultimate Trail-Ready Chair",
  description:
    "Stop sitting on wet logs and damp grass. The CLIQ Portable Chair is engineered for active walkers and campers who need serious comfort without the bulk.",
  affiliate:
    "https://www.cliqproducts.com?utm_source=buzzbassador&utm_medium=referral&utm_campaign=Website_Inbound&buzz_ref=5131376865hSE3o",
  code: "ojadventures",
  specs: [
    {
      Icon: Package,
      title: "Ultra-Compact",
      body: "Folds down to the size of a water bottle, easily slipping into your daypack.",
    },
    {
      Icon: Zap,
      title: "Rapid Setup",
      body: "Deploys in just 5 seconds with an intuitive push-button hub.",
    },
    {
      Icon: Shield,
      title: "Heavy-Duty",
      body: "Aircraft-grade aluminum and ripstop ballistic nylon — supports up to 300 lbs.",
    },
    {
      Icon: Feather,
      title: "Featherlight",
      body: "Weighs only 3.5 lbs, so it won't slow you down on the trails.",
    },
  ],
};

export default function Gear() {
  return (
    <div
      className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-48 sm:pb-40"
      data-testid="gear-page"
    >
      {/* Page header */}
      <div className="text-center">
        <div className="td-section-label">Kit we actually use</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-text mt-1">
          Gear &amp; Partners
        </h1>
        <p className="text-brand-subtle text-sm sm:text-base mt-2 max-w-xl mx-auto">
          A short, honest shortlist of the outdoor kit we take on every walk.
          Buying through these links helps keep TrailDog free — thanks for the
          support.
        </p>
      </div>

      {/* Product card */}
      <article
        className="td-card mt-8 overflow-hidden"
        data-testid="gear-card-cliq"
      >
        {/* Product photo */}
        <div
          className="w-full aspect-[16/9] bg-brand-border/60 border-b border-brand-border overflow-hidden"
          data-testid="gear-cliq-image-placeholder"
        >
          <img
            src="/cliq-chair.jpg"
            alt="CLIQ Portable Chairs in six colourways lined up on a sandy beach with dune grass behind."
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="p-5 sm:p-7">
          {/* Title + headline */}
          <div className="td-section-label" data-testid="gear-cliq-title">
            {CLIQ.title}
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text mt-1 leading-tight">
            {CLIQ.headline}
          </h2>
          <p className="text-brand-text/90 text-[15px] leading-relaxed mt-3">
            {CLIQ.description}
          </p>

          {/* Key specs */}
          <div className="td-section-label mt-6">Key specs</div>
          <ul
            className="mt-3 grid gap-3 sm:grid-cols-2"
            data-testid="gear-cliq-specs"
          >
            {CLIQ.specs.map(({ Icon, title, body }) => (
              <li
                key={title}
                className="flex items-start gap-3 p-3 rounded-2xl bg-brand-sand border border-brand-border"
              >
                <span className="shrink-0 w-10 h-10 rounded-xl bg-brand-primary/10 text-brand-primary flex items-center justify-center">
                  <Icon size={18} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-base text-brand-text leading-tight">
                    {title}
                  </div>
                  <p className="text-xs sm:text-[13px] text-brand-subtle leading-snug mt-0.5">
                    {body}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          {/* Referral code — highlighted block */}
          <div
            className="mt-6 rounded-2xl border border-dashed border-brand-accent/60 bg-brand-accent/10 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3"
            data-testid="gear-cliq-referral"
          >
            <span className="shrink-0 w-10 h-10 rounded-xl bg-brand-accent text-white flex items-center justify-center">
              <Tag size={18} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-heading text-base text-brand-text leading-tight">
                Save with our code
              </div>
              <p className="text-xs sm:text-sm text-brand-subtle leading-snug mt-0.5">
                Use it at checkout on cliqproducts.com.
              </p>
            </div>
            <code
              className="shrink-0 self-start sm:self-auto px-3 py-1.5 rounded-full bg-brand-surface border border-brand-accent/50 text-brand-accent font-mono text-sm font-semibold tracking-wide"
              data-testid="gear-cliq-code"
            >
              {CLIQ.code}
            </code>
          </div>

          {/* CTA */}
          <ExternalLink
            href={CLIQ.affiliate}
            testid="gear-cliq-cta"
            className="td-btn mt-6 w-full !py-3 !px-5 !text-base font-medium inline-flex items-center justify-center gap-2 whitespace-nowrap"
          >
            Shop CLIQ Chair
            <ExternalLinkIcon size={16} />
          </ExternalLink>

          <p className="text-[11px] text-brand-subtle text-center mt-3">
            Affiliate link — clicking through supports OJ Adventures &amp; TrailDog at no extra cost to you.
          </p>
        </div>
      </article>
    </div>
  );
}
